/**
 * OpenAI (ChatGPT Ads) Measurement Pixel — marketing pages ONLY.
 *
 * Why not a static <script> in index.html: SplashPad's Privacy Policy promises
 * no advertising trackers on the Service itself (parent portal, coach portal,
 * coaches' public booking pages, children's data). Ads attribution only needs
 * the pages an ad click can land on and convert from, so the SDK is injected
 * at runtime — into <head>, exactly like OpenAI's snippet — the first time the
 * visitor is on a marketing route, and `measure` calls are only ever made
 * from marketing routes. Portal / booking pages never load or ping it.
 *
 * Docs: https://developers.openai.com/ads/measurement-pixel
 *   oaiq("init", { pixelId, debug })
 *   oaiq("measure", "page_viewed", { type: "contents", contents: [...] })
 *   oaiq("measure", "registration_completed", { type: "customer_action" })
 *   oaiq("measure", "trial_started", { type: "plan_enrollment", plan_id })
 *
 * Pixel IDs are public identifiers (they ship in page HTML by design), so the
 * default lives here; REACT_APP_OPENAI_ADS_PIXEL_ID overrides it per env.
 */

export const OPENAI_PIXEL_ID = process.env.REACT_APP_OPENAI_ADS_PIXEL_ID || 'EgyF3FvoWg7huZx86u58pr';
const SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
const PRODUCTION_HOSTS = ['splashpad.pro', 'www.splashpad.pro'];

// Marketing site = everything an ad can land on. Deliberately excluded:
// /login, /coach/* (coaches' booking pages), /parent/*, /instructor/*,
// /staff/*, /admin/*, /find-instructor (parent directory), legal + privacy
// pages, invoices/invites.
const MARKETING_EXACT = new Set([
    '/', '/signup', '/pricing', '/features', '/customers', '/about', '/blog', '/switch',
    '/best-swim-school-software', '/swim-lesson-registration-software', '/for-swim-instructors',
]);
const MARKETING_PREFIXES = ['/blog/', '/vs/'];

export function isMarketingPath(pathname) {
    if (!pathname) return false;
    const p = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    if (MARKETING_EXACT.has(p)) return true;
    return MARKETING_PREFIXES.some((pre) => p.startsWith(pre));
}

/** debug=true prints SDK activity to the browser console — on for Preview /
 *  localhost so the install can be verified, off on the live site. */
export function pixelDebugEnabled() {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    return !PRODUCTION_HOSTS.includes(host) && !host.endsWith('.splashpad.pro');
}

let loaded = false;

/** Global Privacy Control — when the browser sends it we don't load the
 *  pixel at all (promised in Privacy Policy §4). */
export function gpcOptedOut() {
    try {
        return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
    } catch {
        return false;
    }
}

/** Inject OpenAI's snippet into <head> (idempotent). Returns the queue fn,
 *  or null when the visitor has opted out via GPC. */
export function loadAdsPixel() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;
    if (gpcOptedOut()) return null;
    if (loaded && window.oaiq) return window.oaiq;
    loaded = true;
    if (!window.oaiq) {
        // Verbatim equivalent of OpenAI's install snippet.
        const q = function oaiq() { q.q.push(arguments); }; // eslint-disable-line prefer-rest-params
        q.q = [];
        window.oaiq = q;
        const js = document.createElement('script');
        js.async = true;
        js.src = SDK_URL;
        js.setAttribute('data-openai-pixel', OPENAI_PIXEL_ID);
        const head = document.head || document.getElementsByTagName('head')[0];
        head.insertBefore(js, head.firstChild); // "preferably near the top"
        window.oaiq('init', { pixelId: OPENAI_PIXEL_ID, debug: pixelDebugEnabled() });
    }
    return window.oaiq;
}

/** True once the SDK has been injected on this page load. */
export function adsPixelLoaded() {
    return loaded && typeof window !== 'undefined' && !!window.oaiq;
}

function measure(eventName, data, options) {
    try {
        const oaiq = loadAdsPixel();
        if (!oaiq) return false;
        if (options) oaiq('measure', eventName, data, options);
        else oaiq('measure', eventName, data);
        return true;
    } catch {
        return false; // measurement must never break the page
    }
}

/** One page_viewed per marketing route change (SPA — the SDK doesn't auto-track). */
export function trackAdsPageView(pathname) {
    if (!isMarketingPath(pathname)) return false;
    return measure('page_viewed', {
        type: 'contents',
        contents: [{ id: pathname, name: (typeof document !== 'undefined' && document.title) || pathname, content_type: 'page' }],
    });
}

/** Coach account created (fires on /signup, right after the account exists). */
export function trackCoachSignup(userId) {
    return measure('registration_completed', { type: 'customer_action' }, userId ? { event_id: `signup_${userId}` } : undefined);
}

/** Coach started their 30-day trial (subscription created on /signup). */
export function trackTrialStarted(planId, userId) {
    return measure('trial_started', { type: 'plan_enrollment', plan_id: planId || 'unknown' }, userId ? { event_id: `trial_${userId}` } : undefined);
}

/**
 * Coach upgraded to (or subscribed directly to) the paid Pro plan — the
 * revenue conversion. Fires from the coach's own billing flow, so this is
 * the ONE place the pixel is loaded inside the coach portal; it sends only
 * the plan id, amount and a pseudonymous event id (disclosed in Privacy
 * Policy §4). Never fires for parents.
 */
export function trackProUpgrade(amountCents, userId) {
    const day = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const data = { type: 'plan_enrollment', plan_id: 'pro', currency: 'USD' };
    const amount = Math.round(Number(amountCents));
    if (Number.isFinite(amount) && amount >= 0) data.amount = amount;
    return measure('subscription_created', data, userId ? { event_id: `pro_upgrade_${userId}_${day}` } : undefined);
}
